import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  TextField,
  IconButton,
  InputAdornment,
  Stack,
  Button
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import PaymentIcon from '@mui/icons-material/Payment';
import { useNavigate, useLocation } from 'react-router-dom';
import { ReactComponent as CinchLogo } from '../cinch.svg';
import DeviceService from '../services/deviceService';
import CompanyLogo from '../components/CompanyLogo';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useMember } from '../contexts/MemberContext';
import CashierHeader from '../components/CashierHeader';
import KeypadContainer from '../components/KeypadContainer';

/**
 * Cashier Transaction page for entering transaction amount
 * Features a numeric keypad and amount input
 */
const CashierTransactionPage = () => {
  const [amountInCents, setAmountInCents] = useState(0);
  const [loading, setLoading] = useState(false);
  const { showError, showSuccess } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const cashierPin = location.state?.cashierPin;

  const { orientation, setOrientation, member, selectedOffer } = useMember();

  useEffect(() => {
    if (orientation === 1) {
      // the device was flipped over so navigate to the member dashboard
      navigate('/member-dashboard');
    }
  }, [orientation]);

  // Handle user icon click to switch to customer side
  const handlePersonClick = () => {
    setOrientation(1); // Flip orientation to show member dashboard
  };

  // Handle keypad button press
  const handleKeyPress = (value) => {
    if (typeof value === 'number') {
      // Add digit to amount in cents
      // Prevent overflow (max $999,999.99)
      if (amountInCents < 9999999) {
        setAmountInCents(prev => prev * 10 + value);
      }
    }
  };

  // Handle amount submission
  const handleSubmit = async () => {
    // Allow $0.00 amount
    const amountInDollars = (amountInCents / 100).toFixed(2);

    // Validate that we have the cashier PIN
    if (!cashierPin) {
      showError('Missing cashier PIN. Please log in again.');
      navigate('/cashier-login');
      return;
    }

    setLoading(true);

    try {
      // Process the transaction with the cashier PIN
      // Check if member exists
      if (!member || !member.id) {
        showError('Member information not found. Please ensure a member is logged in.');
        navigate('/phone-entry');
        return;
      }

      const response = await DeviceService.processTransaction(
        cashierPin,
        member.id,
        amountInDollars,
        selectedOffer?.customer_coupon_id,
        selectedOffer?.id
      );

      showSuccess(`Transaction of $${amountInDollars} submitted successfully`);

      // Navigate to the transaction confirmation page
      navigate('/transaction-confirmation', {
        state: { pointsEarned: response.points_earned, totalPoints: response.total_points, amount: amountInDollars, }
      });

      // Reset amount for when they return to this page
      setAmountInCents(0);

    } catch (err) {
      console.error(err);
      showError(err.message || 'Error processing transaction. Please try again.');
    }

    setLoading(false);
  };

  // Navigate back to member dashboard
  const handleHomeClick = () => {
    navigate('/landing');
  };

  // Format the amount for display
  const formatAmount = () => {
    return (amountInCents / 100).toFixed(2);
  };

  return (
    <Container sx={{
      height: '100vh',
      width: '100vw',
      maxWidth: '100vw !important',
      display: 'flex',
      flexDirection: 'row',
      padding: '0 !important',
      margin: '0 !important',
    }}>
      <Grid container sx={{
        height: '100%',
        width: '100%'
      }}>
        <Grid item size={6} sx={{
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'row',
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            px: 3
          }}>
            <CashierHeader />

            <Box sx={{ maxWidth: '800px', width: '100%' }}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                align="center"
                fontWeight="bold"
                sx={{ mb: 6, mt: 6 }}
              >
                How much did the customer spend before taxes?
              </Typography>

              <Typography
                variant="h6"
                component="h2"
                gutterBottom
                align="center"
                sx={{ mb: 4 }}
              >
                Pre-Tax Amount
              </Typography>

              <TextField
                fullWidth
                variant="outlined"
                value={formatAmount()}
                InputProps={{
                  readOnly: true,
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  sx: {
                    fontSize: '2rem',
                    textAlign: 'center',
                    input: { textAlign: 'center' }
                  }
                }}
              />
            </Box>
            <Box pb={2} display="flex" justifyContent="center" alignItems="center">
              <Typography variant="body3" color="text.secondary">
                powered by
              </Typography>
              <Box ml={1} display="flex" alignItems="center">
                <CinchLogo
                  width={75}
                  style={{ marginRight: '4px' }}
                />
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item size={6}>
          <KeypadContainer
            loading={loading}
            canSubmit={true}
            onKeyPress={handleKeyPress}
            onSubmit={handleSubmit}
            onBackspace={() => setAmountInCents(prev => Math.floor(prev / 10))}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default CashierTransactionPage;